import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { writeBack } from "../lib/smart";
import type {
  AppConfig,
  AssessmentPayload,
  DecisionResult,
  Recommendation,
  SessionInfo,
  Severity,
  SmartSession,
  WeightStatus,
} from "../lib/types";
import BmiChart from "./BmiChart";
import { fmtDate, useI18n } from "../lib/i18n";
import AiPanel from "./AiPanel";

type Decision = { kind: "accept" } | { kind: "decline"; reason: string };

const CATEGORIES = ["all", "safety", "comorbidity", "labs", "treatment", "medication", "referral", "screening"];

export default function PatientView({
  patientId,
  initial,
  smart,
  config,
  session,
}: {
  patientId: string;
  initial?: AssessmentPayload;
  smart?: SmartSession;
  config: AppConfig | null;
  session: SessionInfo;
}) {
  const [data, setData] = useState<AssessmentPayload | null>(initial ?? null);
  const [err, setErr] = useState<string | null>(null);
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  const [filter, setFilter] = useState("all");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<DecisionResult | null>(null);
  // The decision set last recorded, so pressing Confirm again cannot record it twice.
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [ehrStatus, setEhrStatus] = useState<string | null>(null);
  const { t } = useI18n();

  useEffect(() => {
    if (initial) return;
    api.assessment(patientId).then(setData).catch((e: Error) => setErr(e.message));
  }, [patientId, initial]);

  const recs = data?.assessment.recommendations ?? [];
  const shown = useMemo(() => recs.filter((r) => filter === "all" || r.category === filter), [recs, filter]);
  const presentCats = useMemo(() => new Set(recs.map((r) => r.category)), [recs]);

  if (err) return <p className="error">Could not load patient: {err}</p>;
  if (!data) return <p className="muted">{t("loading")}</p>;

  const { patient, assessment: a, data_quality: dq } = data;
  const ws = (a.weight_status && "bmi" in a.weight_status ? a.weight_status : null) as WeightStatus | null;
  const actionable = recs.filter((r) => r.severity !== "info" || r.orders.length > 0);
  const decidedCount = Object.keys(decisions).length;
  const declinedMissingReason = Object.values(decisions).some((d) => d.kind === "decline" && d.reason.trim().length < 2);
  const decisionsKey = JSON.stringify(decisions);
  const alreadySaved = savedKey === decisionsKey;
  const canSubmit = decidedCount > 0 && !declinedMissingReason && !submitting && !alreadySaved;

  const setDecision = (id: string, d: Decision | null) =>
    setDecisions((prev) => {
      const next = { ...prev };
      if (d) next[id] = d;
      else delete next[id];
      return next;
    });

  const acceptAllRoutine = () =>
    setDecisions((prev) => {
      const next = { ...prev };
      for (const r of actionable) if (!next[r.id] && r.severity !== "urgent") next[r.id] = { kind: "accept" };
      return next;
    });

  async function submit() {
    setSubmitting(true);
    setErr(null);
    setEhrStatus(null);
    try {
      const accepted = Object.entries(decisions).filter(([, d]) => d.kind === "accept").map(([id]) => id);
      const declined = Object.entries(decisions)
        .filter(([, d]) => d.kind === "decline")
        .map(([id, d]) => ({ id, reason: (d as { reason: string }).reason }));
      // The server signs with the authenticated user; there is no free-text signer.
      const res = await api.decisions(patient.id, {
        accepted,
        declined,
        encounter_ref: smart?.encounterRef ?? null,
        writeback: "preview",
      });
      setResult(res);
      setSavedKey(decisionsKey);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function postToEhr() {
    if (!result) return;
    setEhrStatus("Posting to EHR…");
    try {
      await writeBack(result.transaction);
      setEhrStatus("Written to EHR as draft orders, draft care plan and a preliminary note.");
      api.writebackResult(patient.id, true, "").catch(() => undefined);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setEhrStatus(`EHR write-back failed: ${msg}`);
      api.writebackResult(patient.id, false, msg.slice(0, 400)).catch(() => undefined);
    }
  }

  return (
    <div>
      <div className="patient-head">
        <h1>{patient.name ?? patient.id}</h1>
        <span className="muted">
          {patient.age_years.toFixed(1)} y · {t(patient.sex === "male" ? "male" : "female")} · {t("dob")}{" "}
          <span dir="ltr">{fmtDate(patient.birth_date)}</span>
        </span>
        {patient.display_id && <span className="pill eid" dir="ltr" title="Emirates ID (masked)">{patient.display_id}</span>}
        <span className="pill">{patient.source}</span>
        {!smart && (
          <a href="#/" className="small">
            {t("backToPatients")}
          </a>
        )}
      </div>

      <div className="layout">
        <div className="stack">
          <section className="card">
            <div className="metrics">
              <Metric label={t("bmi")} value={ws ? ws.bmi.toFixed(1) : "–"} />
              <Metric label={t("bmiZ")} value={ws ? `${ws.z >= 0 ? "+" : ""}${ws.z.toFixed(2)}` : "–"}
                sub={ws?.reference} />
              <Metric label={t("percentile")} value={ws ? (ws.percentile > 99.9 ? ">99.9" : ws.percentile.toFixed(1)) : "–"} />
              <Metric
                label={t("bp")}
                value={a.blood_pressure ? `${a.blood_pressure.systolic.toFixed(0)}/${a.blood_pressure.diastolic.toFixed(0)}` : "–"}
              />
            </div>
            <div className="row" style={{ marginBottom: 8 }}>
              <span className={`wclass ${ws?.class ?? ""}`}>{ws ? ws.label : "No current BMI"}</span>
              {a.blood_pressure && <span className="muted small">BP: {a.blood_pressure.label}</span>}
            </div>
            {a.bmi_trend.length > 0 && <BmiChart sex={patient.sex} trend={a.bmi_trend} ageNow={a.age_years} />}
          </section>

          <section className="stack" aria-label="Recommendations">
            <div className="row">
              <h2 style={{ margin: 0, fontSize: 17 }}>{t("recsHeading")}</h2>
              <span className="muted small">{t("reviewEach", { n: recs.length })}</span>
              <div className="spacer" style={{ flex: 1 }} />
              <span className="muted small">{t("urgentIndividual")}</span>
              <button className="btn small" onClick={acceptAllRoutine}>
                {t("acceptAll")}
              </button>
            </div>
            <div className="filters" role="group" aria-label="Filter by category">
              {CATEGORIES.filter((c) => c === "all" || presentCats.has(c)).map((c) => (
                <button key={c} className="chip" aria-pressed={filter === c} onClick={() => setFilter(c)}>
                  {c}
                </button>
              ))}
            </div>
            {shown.map((r) => (
              <RecCard key={r.id} rec={r} decision={decisions[r.id]} onDecide={(d) => setDecision(r.id, d)} />
            ))}

            <div className="decision-bar">
              <span className="small">
                {t("signingAs")} <strong>{session.display_name}</strong>
              </span>
              <span className="muted small">
                {t("decided", { n: decidedCount })}
                {declinedMissingReason && ` · ${t("needReason")}`}
                {alreadySaved && ` · ${t("recorded")}`}
              </span>
              <div style={{ flex: 1 }} />
              <button className="btn primary" disabled={!canSubmit} onClick={submit}>
                {submitting ? t("saving") : t("confirm")}
              </button>
            </div>
            {err && <p className="error">{err}</p>}
            {result && <DecisionOutput result={result} smart={!!smart} onPost={postToEhr} ehrStatus={ehrStatus} />}
          </section>
        </div>

        <aside className="stack">
          <AiPanel patientId={patient.id} config={config} />
          <ClinicalData data={data} />
          {a.data_gaps.length > 0 && (
            <section className="card">
              <h3>{t("missingData")}</h3>
              <ul className="gaps small">
                {a.data_gaps.map((g) => (
                  <li key={g}>{g}</li>
                ))}
              </ul>
            </section>
          )}
          {dq && <DataQuality dq={dq} />}
          <p className="muted small">
            {a.guideline} · engine {a.engine_version} · evaluated <span dir="ltr">{fmtDate(a.as_of)}</span>. {t("footer")}{" "}
            {t("clinicalEnglish")}
          </p>
        </aside>
      </div>
    </div>
  );
}

function Metric({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="metric">
      <div className="label">{label}</div>
      <div className="value" dir="ltr">{value}</div>
      {sub && <div className="label">{sub}</div>}
    </div>
  );
}

function RecCard({
  rec: r,
  decision,
  onDecide,
}: {
  rec: Recommendation;
  decision?: Decision;
  onDecide: (d: Decision | null) => void;
}) {
  const [open, setOpen] = useState(r.severity === "urgent" || r.severity === "important");
  const { t } = useI18n();
  // Engine content is English; dir="auto" keeps it readable inside the Arabic (RTL) interface.
  return (
    <article className={`rec band ${r.severity} ${decision?.kind === "decline" ? "declined" : ""}`}>
      <div className="head">
        <span className={`tier ${r.severity as Severity}`}>{t(r.severity)}</span>
        <span className="title" lang="en" dir="auto">{r.title}</span>
        <span className="kas">
          {r.kas}
          {r.grade ? ` · Grade ${r.grade}` : ""}
        </span>
      </div>
      <div className="small" lang="en" dir="auto">{r.rationale}</div>
      {open && (
        <>
          {r.actions.length > 0 && (
            <ul className="small" lang="en" dir="auto">
              {r.actions.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          )}
          {r.orders.length > 0 && (
            <div className="orders">Draft orders if accepted: {r.orders.map((o) => o.display).join("; ")}</div>
          )}
          <div className="muted small">
            Source: {r.source}
            {r.strength ? ` · ${r.strength}` : ""}
          </div>
        </>
      )}
      <div className="decide">
        <div className="seg" role="group" aria-label={`Decision for ${r.title}`}>
          <button className="accept" aria-pressed={decision?.kind === "accept"}
            onClick={() => onDecide(decision?.kind === "accept" ? null : { kind: "accept" })}>
            {t("accept")}
          </button>
          <button className="decline" aria-pressed={decision?.kind === "decline"}
            onClick={() => onDecide(decision?.kind === "decline" ? null : { kind: "decline", reason: "" })}>
            {t("notNow")}
          </button>
        </div>
        {decision?.kind === "decline" && (
          <input type="text" placeholder={t("reasonRequired")} value={decision.reason} dir="auto" style={{ flex: 1, minWidth: 180 }}
            onChange={(e) => onDecide({ kind: "decline", reason: e.target.value })} aria-label="Reason" />
        )}
        <button className="btn small" onClick={() => setOpen(!open)} aria-expanded={open}>
          {open ? t("less") : t("details")}
        </button>
      </div>
    </article>
  );
}

function DecisionOutput({
  result,
  smart,
  onPost,
  ehrStatus,
}: {
  result: DecisionResult;
  smart: boolean;
  onPost: () => void;
  ehrStatus: string | null;
}) {
  const [tab, setTab] = useState<"note" | "fhir" | "hl7">("note");
  const counts = result.transaction.entry.reduce<Record<string, number>>((acc, e) => {
    acc[e.resource.resourceType] = (acc[e.resource.resourceType] ?? 0) + 1;
    return acc;
  }, {});
  return (
    <section className="card">
      <h3>Decisions recorded ({result.recorded})</h3>
      <p className="small muted">
        Write-back package: {Object.entries(counts).map(([k, v]) => `${v} ${k}`).join(", ")}. Orders are drafts for
        signing in the EHR.
      </p>
      <div className="row" style={{ marginBottom: 10 }}>
        {smart ? (
          <button className="btn primary" onClick={onPost}>
            Write to EHR
          </button>
        ) : (
          <span className="small muted">Launch from the EHR (SMART) to write back directly, or send the HL7 ORU.</span>
        )}
        {ehrStatus && <span className="small">{ehrStatus}</span>}
      </div>
      <div className="tabs" role="tablist">
        {(["note", "fhir", "hl7"] as const).map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)}>
            {t === "note" ? "Visit note" : t === "fhir" ? "FHIR transaction" : "HL7 v2 ORU"}
          </button>
        ))}
      </div>
      <pre className="code">
        {tab === "note"
          ? result.note
          : tab === "fhir"
            ? JSON.stringify(result.transaction, null, 2)
            : result.hl7_oru.replace(/\r/g, "\n")}
      </pre>
    </section>
  );
}

function ClinicalData({ data }: { data: AssessmentPayload }) {
  const { patient, assessment: a } = data;
  const { t } = useI18n();
  return (
    <section className="card">
      <h3>{t("clinicalData")}</h3>
      <dl className="kv" lang="en" dir="auto">
        {a.blood_pressure && (
          <>
            <dt>BP</dt>
            <dd>
              {a.blood_pressure.systolic.toFixed(0)}/{a.blood_pressure.diastolic.toFixed(0)} ({fmtDate(a.blood_pressure.date)}) —{" "}
              {a.blood_pressure.label}
              <div className="muted small">{a.blood_pressure.note}</div>
            </dd>
          </>
        )}
        {a.lab_findings.map((f) => (
          <FragmentRow key={f.code} k={f.name} v={`${f.display} (${fmtDate(f.date)})`} note={f.detail} status={f.status} />
        ))}
        {patient.medications.length > 0 && <FragmentRow k="Medications" v={patient.medications.join("; ")} />}
        {patient.conditions.length > 0 && <FragmentRow k="Problems" v={patient.conditions.join(", ").replace(/_/g, " ")} />}
        {patient.symptoms.length > 0 && <FragmentRow k="ROS positives" v={patient.symptoms.join(", ").replace(/_/g, " ")} />}
        {patient.risk_factors.length > 0 && (
          <FragmentRow k="Risk factors" v={patient.risk_factors.join(", ").replace(/_/g, " ")} />
        )}
        {patient.ihblt_hours != null && <FragmentRow k="IHBLT hours" v={`${patient.ihblt_hours} of 26`} />}
      </dl>
      {a.lab_findings.length === 0 && <p className="muted small">No relevant labs on record.</p>}
    </section>
  );
}

function FragmentRow({ k, v, note, status }: { k: string; v: string; note?: string; status?: string }) {
  const color =
    status === "abnormal" || status === "diabetes_range"
      ? "var(--critical)"
      : status === "borderline" || status === "prediabetes_range"
        ? "var(--high)"
        : undefined;
  return (
    <>
      <dt>{k}</dt>
      <dd style={{ color }}>
        {v}
        {note && <div className="muted small">{note}</div>}
      </dd>
    </>
  );
}

function DataQuality({ dq }: { dq: Record<string, unknown> }) {
  const lists = (["implausible", "unmapped_codes", "unit_issues", "missing"] as const)
    .map((k) => [k, (dq[k] as string[] | undefined) ?? []] as const)
    .filter(([, v]) => v.length > 0);
  if (lists.length === 0) return null;
  return (
    <section className="card dq">
      <details>
        <summary>
          <strong>Data quality</strong> <span className="muted">({lists.reduce((n, [, v]) => n + v.length, 0)} items)</span>
        </summary>
        {lists.map(([k, v]) => (
          <div key={k} style={{ marginTop: 8 }}>
            <div className="muted">{k.replace(/_/g, " ")}</div>
            <ul>
              {v.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </div>
        ))}
      </details>
    </section>
  );
}
